import { useTranslation } from 'react-i18next'

import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { FileUpload, ItemTemplateOptions } from 'primereact/fileupload'
import { Message } from 'primereact/message'
import { classNames } from 'primereact/utils'

import { UploadPanelFormValuesNames } from '../common/UploadPanelFormValues'

type UploadPanelProps = {
  formik: any
}

export const UploadPanel: React.FC<UploadPanelProps> = ({ formik }) => {
  const { t, i18n } = useTranslation()

  const uploadOptions = {
    icon: '',
    iconOnly: true,
    className: 'hidden',
  }

  const cancelOptions = {
    icon: '',
    iconOnly: true,
    className: 'hidden',
  }

  const fileItemTemplate = (file: any, options: ItemTemplateOptions) => {
    return (
      <div className="flex align-items-center flex-wrap">
        <div className="flex align-items-center">
          <img alt={''} src={file.objectURL} width={100} />
          <span className="flex flex-column text-left">
            <span className="font-bold">{file.name}</span>
            <span className="text-sm mt-1">{options.formatSize}</span>
            <span className="text-sm mt-1">
              {new Date().toLocaleDateString()}
            </span>
          </span>
        </div>
        <Button
          type="button"
          icon="pi pi-times"
          className="p-button-outlined p-button-rounded p-button-danger ml-auto"
          onClick={options.onRemove}
        />
      </div>
    )
  }

  const isFormFieldValid = (name: UploadPanelFormValuesNames) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: UploadPanelFormValuesNames) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  const Footer = () => {
    return (
      <>
        <div className="flex pt-4 justify-content-end">
          <Button
            label={t(
              'masterDataManagement.companyCodes.table.upload.dialog.uploadPanel.footer.next',
            )}
            icon={PrimeIcons.ARROW_RIGHT}
            iconPos="right"
            onClick={formik.submitForm}
          />
        </div>
      </>
    )
  }

  const downloadSample = () => {
    const fileURL = `/files/sample_companycodes_${i18n.language}.csv`
    const fileLink = document.createElement('a')
    fileLink.href = fileURL
    fileLink.setAttribute(
      'download',
      `company_codes_sample_${i18n.language.toUpperCase()}.csv`,
    )
    document.body.appendChild(fileLink)
    fileLink.click()
    document.body.removeChild(fileLink)
  }

  const messageContent = (
    <div className="flex align-items-center gap-2 w-full">
      <i className="p-message-icon pi pi-exclamation-circle"></i>
      <div className="flex flex-grow-1 justify-content-between">
        <div>
          <strong>
            {t(
              'masterDataManagement.companyCodes.table.upload.dialog.uploadPanel.message.title',
            )}
          </strong>
          <br />
          <span>
            {t(
              'masterDataManagement.companyCodes.table.upload.dialog.uploadPanel.message.description',
            )}
          </span>
        </div>
        <div>
          <Button
            type="button"
            className="p-button-text p-button-rounded"
            icon={PrimeIcons.DOWNLOAD}
            onClick={() => downloadSample()}
          />
        </div>
      </div>
    </div>
  )

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="flex flex-column gap-4">
        <Message
          className="w-full justify-content-start p-message my-0"
          severity="info"
          content={messageContent}
        />

        <div className="flex flex-column gap-2">
          <label
            htmlFor="csv"
            className={classNames('text-sm', {
              'p-error': !isFormFieldValid('csv'),
            })}
          >
            {t(
              'masterDataManagement.companyCodes.table.upload.dialog.uploadPanel.fields.csv',
            )}
          </label>
          <FileUpload
            id="csv"
            uploadOptions={uploadOptions}
            cancelOptions={cancelOptions}
            itemTemplate={fileItemTemplate}
            multiple={false}
            accept=".csv"
            onRemove={() => formik.setFieldValue('csv', undefined)}
            onSelect={e => formik.setFieldValue('csv', e.files[0])}
            emptyTemplate={t(
              'masterDataManagement.companyCodes.table.upload.dialog.uploadPanel.fileUpload.emptyTemplate',
            )}
            maxFileSize={10 * 1024 * 1024} //10 MB
            invalidFileSizeMessageSummary={t(
              'masterDataManagement.companyCodes.table.upload.dialog.uploadPanel.fileUpload.invalidFileSizeMessageSummary',
            )}
            invalidFileSizeMessageDetail={t(
              'masterDataManagement.companyCodes.table.upload.dialog.uploadPanel.fileUpload.invalidFileSizeMessageDetail',
            )}
          />
          {getFormErrorMessage('csv')}
        </div>
      </div>
      <Footer />
    </form>
  )
}
